// ---------------------------------------------------------------------------
// Flow Ownership — resolves which block a named flow belongs to.
//
// Single source of truth for flow→block routing, shared by:
//   - FlowStepPanel (display: "Block: <owner>" + step seeding)
//   - auto-save-bridge (persistence: which block's `flow` field receives a
//     step-list save — F58 replaced the silent first-block fallback)
//
// Ownership rule (must stay consistent with toLoadedApp's first-declarer
// flow merge in services/project-api.ts, or save→reload round-trips break):
//   1. The first block whose flow map declares the flow name (full name,
//      then the bare name after the last '.').
//   2. Else, the first block whose events bind the flow (covers flows
//      created in the Behavior surface and bound before their first save).
//   3. Else null — callers apply their own documented default.
// ---------------------------------------------------------------------------

import { foldBundleEventBindings } from '@cogo/publisher-core/event-binding-shape'
import type { Flow, EventBinding } from '@cogo/types-v2'
import type { FlowMeta } from '../models/flow-models'

export interface OwnedBlock {
  flow?: Record<string, unknown>
  events?: Record<string, unknown>
}

export interface FlowOwnerResolution {
  blockName: string
  /** Key under which the owner block declares (or should declare) the flow. */
  flowKey: string
  /** The declared flow, when resolution came from a flow-map declaration. */
  flow?: Flow
}

export interface FlowTriggerInfo {
  elementId: string
  eventType: string
  blockName: string
}

/**
 * Find the first block whose flow map declares `flowName` (exact key match,
 * both flat `{ name: def }` and wrapped `{ flows: { name: def } }` shapes).
 */
export function findFlowOwner(
  blocks: Record<string, { flow?: Record<string, unknown> }>,
  flowName: string,
): { blockName: string; flow: Flow } | null {
  for (const [blockName, block] of Object.entries(blocks)) {
    if (!block.flow) continue
    // Direct: block.flow[flowName]
    if (block.flow[flowName] && typeof block.flow[flowName] === 'object') {
      return { blockName, flow: block.flow[flowName] as Flow }
    }
    // Nested: block.flow.flows[flowName] (Coogo JSON standard structure)
    const flows = block.flow.flows as Record<string, Flow> | undefined
    if (flows && flows[flowName]) {
      return { blockName, flow: flows[flowName] }
    }
  }
  return null
}

/**
 * Search through block events to find which element/event triggers a given
 * flow name. Supports both nested ({ elementId: { eventType: binding } })
 * and flat ("elementId:eventType" → binding) event formats.
 */
export function findFlowTriggers(
  blocks: Record<string, { events?: Record<string, Record<string, EventBinding>> }>,
  flowName: string,
): FlowTriggerInfo[] {
  const triggers: FlowTriggerInfo[] = []

  for (const [blockName, block] of Object.entries(blocks)) {
    const events = block.events as Record<string, unknown> | undefined
    if (!events) continue

    // Task 4 / authority-repair (2026-08-09, round 2): fold first. The old
    // `events.bindings ?? events` fell back to the artifact itself, so an
    // `elements`-shaped block was iterated with elementId === "elements".
    const folded = foldBundleEventBindings(events)
    const eventEntries = (Object.keys(folded).length > 0 ? folded : events) as Record<string, unknown>
    for (const [key, value] of Object.entries(eventEntries)) {
      if (!value || typeof value !== 'object') continue
      const eventMap = value as Record<string, unknown>
      const isNestedFormat = !('flow' in eventMap) && !('action' in eventMap) &&
        Object.values(eventMap).some((v) => v && typeof v === 'object' && ('flow' in (v as object) || 'action' in (v as object)))

      if (isNestedFormat) {
        for (const [eventType, bindingRaw] of Object.entries(eventMap)) {
          const binding = bindingRaw as EventBinding
          if (binding.flow === flowName) {
            triggers.push({ elementId: key, eventType, blockName })
          }
        }
      } else if ('flow' in eventMap || 'action' in eventMap) {
        const binding = eventMap as EventBinding
        if (binding.flow === flowName || binding.action === flowName) {
          const lastColon = key.lastIndexOf(':')
          triggers.push({
            elementId: lastColon > 0 ? key.slice(0, lastColon) : key,
            eventType: lastColon > 0 ? key.slice(lastColon + 1) : 'tap',
            blockName,
          })
        }
      }
    }
  }

  return triggers
}

// ---------------------------------------------------------------------------
// Binding rewrite — the write twin of findFlowTriggers (F3). Renaming or
// deleting a flow must rewrite EVERY representation a binding can live in:
//   1. block-level `block.events` — `{ bindings: { "el:evt": binding } }`
//      wrapper, flat `"el:evt" → binding`, nested `el → evt → binding`,
//      with binding values either `{ flow / action }` objects or the string
//      shorthand (cross-reference.ts resolves all of these).
//   2. element-level `element.events` inside the block ui root tree —
//      `Record<eventType, string | EventBinding>` (collectElementEvents in
//      @cogo/types-v2 — the DURABLE copy the renderer and exporters read).
// `rename === null` means delete the binding instead of renaming it.
// Both helpers mutate IN PLACE (callers invoke them on immer drafts) unless
// `dryRun` is set, and report what matched so callers can snapshot priors
// and queue the right block-field saves.
// ---------------------------------------------------------------------------

/** Whether a single binding value (object or string shorthand) references one
 *  of the flow names being rewritten. Exact, case-sensitive equality — the
 *  same rule findFlowTriggers applies. */
function bindingReferencesFlow(value: unknown, names: ReadonlySet<string>): boolean {
  if (typeof value === 'string') return names.has(value)
  if (!value || typeof value !== 'object') return false
  const binding = value as { flow?: unknown; action?: unknown }
  return (
    (typeof binding.flow === 'string' && names.has(binding.flow)) ||
    (typeof binding.action === 'string' && names.has(binding.action))
  )
}

/** Rewrite one matched binding in its containing map. String shorthand stays
 *  a string; object bindings get flow/action fields rewritten in place. */
function rewriteBindingAt(
  map: Record<string, unknown>,
  key: string,
  names: ReadonlySet<string>,
  rename: string | null,
): void {
  if (rename === null) {
    delete map[key]
    return
  }
  const value = map[key]
  if (typeof value === 'string') {
    map[key] = rename
    return
  }
  const binding = value as { flow?: unknown; action?: unknown }
  if (typeof binding.flow === 'string' && names.has(binding.flow)) binding.flow = rename
  if (typeof binding.action === 'string' && names.has(binding.action)) binding.action = rename
}

/**
 * Rewrite (or, with `rename === null`, remove) every block-level event
 * binding referencing one of `names`. Returns true when anything matched.
 * Handles all three persisted formats (see section header). With `dryRun`
 * nothing is mutated — used to decide which blocks need a prior snapshot.
 *
 * COR-3: the `bindings` wrapper and the top-level flat/nested entries are
 * NOT mutually exclusive — the F8 dual-write (data-slice's addEventBinding
 * force-creates `events.bindings`) produces mixed shapes ALONGSIDE
 * server-loaded flat/nested legacy keys. Reading them XOR left the sibling
 * format's references unrewritten on rename (dead button: binding points at
 * a name that no longer exists) and undeleted on delete (live ghost binding).
 * Walk BOTH.
 */
export function rewriteFlowEventBindings(
  events: Record<string, unknown> | undefined,
  names: ReadonlySet<string>,
  rename: string | null,
  options?: { dryRun?: boolean },
): boolean {
  if (!events || typeof events !== 'object') return false

  let changed = false
  const rewriteEntries = (entries: Record<string, unknown>, skipWrapperKey: boolean): void => {
    for (const [key, value] of Object.entries(entries)) {
      // The wrapper object itself is walked separately — don't re-interpret
      // it as a nested elementId map on the top-level pass.
      if (skipWrapperKey && key === 'bindings') continue
      if (typeof value === 'string' || bindingReferencesFlow(value, names)) {
        // Flat formats: string shorthand or `{ flow / action }` binding object.
        if (bindingReferencesFlow(value, names)) {
          changed = true
          if (!options?.dryRun) rewriteBindingAt(entries, key, names, rename)
        }
        continue
      }
      if (!value || typeof value !== 'object') continue
      const eventMap = value as Record<string, unknown>
      const isNestedFormat =
        !('flow' in eventMap) &&
        !('action' in eventMap) &&
        Object.values(eventMap).some(
          (v) =>
            typeof v === 'string' ||
            (v && typeof v === 'object' && ('flow' in (v as object) || 'action' in (v as object))),
        )
      if (!isNestedFormat) continue
      for (const [eventType, bindingRaw] of Object.entries(eventMap)) {
        if (!bindingReferencesFlow(bindingRaw, names)) continue
        changed = true
        if (!options?.dryRun) rewriteBindingAt(eventMap, eventType, names, rename)
      }
    }
  }

  const wrapper = (events as { bindings?: unknown }).bindings
  const wrapperIsMap =
    wrapper !== null && typeof wrapper === 'object' && !Array.isArray(wrapper)
  if (wrapperIsMap) rewriteEntries(wrapper as Record<string, unknown>, false)
  rewriteEntries(events as Record<string, unknown>, wrapperIsMap)
  return changed
}

/** One element-level event binding matched by rewriteElementFlowEvents. */
export interface ElementFlowEventMatch {
  elementId: string
  eventType: string
}

interface ElementEventsNode {
  id?: unknown
  events?: Record<string, unknown>
  children?: unknown[]
}

/**
 * Walk a block ui element tree and rewrite (or remove, `rename === null`)
 * every `element.events` entry referencing one of `names`. Returns the
 * matched coordinates so callers can snapshot prior bindings, mirror the
 * change into the page-editor store, and queue the `ui` field save. With
 * `dryRun` nothing is mutated.
 */
export function rewriteElementFlowEvents(
  root: unknown,
  names: ReadonlySet<string>,
  rename: string | null,
  options?: { dryRun?: boolean },
): ElementFlowEventMatch[] {
  const matches: ElementFlowEventMatch[] = []
  const walk = (node: unknown): void => {
    if (!node || typeof node !== 'object') return
    const el = node as ElementEventsNode
    if (el.events && typeof el.events === 'object') {
      for (const [eventType, binding] of Object.entries(el.events)) {
        if (!bindingReferencesFlow(binding, names)) continue
        matches.push({ elementId: typeof el.id === 'string' ? el.id : '', eventType })
        if (!options?.dryRun) rewriteBindingAt(el.events, eventType, names, rename)
      }
    }
    if (Array.isArray(el.children)) {
      for (const child of el.children) walk(child)
    }
  }
  walk(root)
  return matches
}

function bareFlowName(flowName: string): string | null {
  const dotIdx = flowName.lastIndexOf('.')
  return dotIdx > 0 ? flowName.slice(dotIdx + 1) : null
}

/**
 * Flow names that can never be safe object keys (CS-1): a plain bracket
 * assignment with key `"__proto__"` invokes the inherited Object.prototype
 * setter instead of creating an own property — zero own keys, JSON-empty
 * persist, silent data loss — and `constructor` / `prototype` shadow
 * inherited members in equally confusing ways. Rejected at every name
 * ingress (createFlow / renameFlow store guards + the MCP tool handlers);
 * the flow-map write helpers are additionally proto-safe (defense in depth).
 */
const RESERVED_FLOW_NAMES = new Set(['__proto__', 'constructor', 'prototype'])

/** Whether `name` is a reserved (prototype-polluting) flow name. */
export function isReservedFlowName(name: string): boolean {
  return RESERVED_FLOW_NAMES.has(name)
}

/**
 * Whether an editor-store flow name refers to the same flow as a persisted
 * flow name, using the same full-then-bare rule as resolveFlowOwner (this
 * module): an editor flow may be MORE namespaced than the block's declared
 * key (e.g. an agent-created "store-nav.addToCart" persists under the
 * block's "addToCart" declaration on every save), so a bare-name match
 * means the flow IS persisted. Case-insensitive to stay consistent with
 * collectBehaviorSummary's name dedup. Used by BOTH the hydration
 * editor-only-drafts filter and the pill's unsaved count so the flow list
 * and the annotation can never disagree.
 */
export function flowNamesMatch(editorName: string, persistedName: string): boolean {
  const persisted = persistedName.toLowerCase()
  if (editorName.toLowerCase() === persisted) return true
  const dotIdx = editorName.lastIndexOf('.')
  return dotIdx > 0 && editorName.slice(dotIdx + 1).toLowerCase() === persisted
}

/**
 * Symmetric collision check between two flow names. flowNamesMatch is
 * directional (editor name → persisted name); when testing whether a NEW
 * name collides with an EXISTING one we don't know which side is the more
 * namespaced — either direction means both names resolve to the same flow
 * slot under the full-then-bare ownership rule (and hydration would dedupe
 * one against the other) — so check both.
 */
function flowNamesCollide(a: string, b: string): boolean {
  return flowNamesMatch(a, b) || flowNamesMatch(b, a)
}

/**
 * Flow names declared in a block's flow map, unwrapping the standard
 * `{ flows: { name: def } }` nesting (falling back to the flat
 * `{ name: def }` shape) and skipping `_`-prefixed metadata keys — the same
 * unwrap rule resolveFlowOwner applies when looking up declarations.
 */
function declaredFlowNames(block: OwnedBlock): string[] {
  const flowField = block.flow
  if (!flowField || typeof flowField !== 'object') return []
  const nested = (flowField as { flows?: unknown }).flows
  const rawMap =
    nested && typeof nested === 'object' && !Array.isArray(nested)
      ? (nested as Record<string, unknown>)
      : flowField
  return Object.keys(rawMap).filter((key) => !key.startsWith('_'))
}

/**
 * Whether `name` is already taken by another flow — the rename/create guard
 * primitive. A name is taken when it collides (flowNamesCollide: full-then-
 * bare, case-insensitive, either direction) with:
 *   - any editor FlowMeta other than the one identified by `excludeId`, OR
 *   - any flow declared in any block's flow map (`block.flow.flows ??
 *     block.flow`, `_`-prefixed keys skipped).
 *
 * Self-exclusion subtlety: excluding by id alone is not enough for a
 * persisted flow. A hydrated flow's id is `${blockName}:${name}` and its
 * declaration ALSO lives in that block's flow map — so a case-only rename
 * (or a case-variant of a namespaced editor flow whose saves land under the
 * bare key) would be wrongly rejected by its OWN declaration. A block
 * declaration is therefore treated as the excluded flow itself when the
 * excluded FlowMeta's CURRENT name flowNamesMatch-es it. The directional
 * check (editor name → declared key) is deliberate: the editor name may be
 * more namespaced than its declared key, never less, so a declaration that
 * is itself MORE namespaced than the excluded name (e.g. excluded
 * 'addToCart' vs declared 'other.addToCart') is a different flow and still
 * collides.
 */
export function isFlowNameTaken(
  flows: FlowMeta[],
  blocks: Record<string, OwnedBlock>,
  name: string,
  excludeId?: string,
): boolean {
  const excluded = excludeId !== undefined ? flows.find((flow) => flow.id === excludeId) : undefined

  for (const flow of flows) {
    if (excludeId !== undefined && flow.id === excludeId) continue
    if (flowNamesCollide(name, flow.name)) return true
  }

  for (const block of Object.values(blocks)) {
    for (const declared of declaredFlowNames(block)) {
      // The excluded flow's own persisted declaration is itself, not a
      // collision (see doc above — directional on purpose).
      if (excluded && flowNamesMatch(excluded.name, declared)) continue
      if (flowNamesCollide(name, declared)) return true
    }
  }

  return false
}

/**
 * Resolve the owner block for a flow name (see module header for the rule).
 * Returns null when no block declares the flow and no event binds it.
 */
export function resolveFlowOwner(
  blocks: Record<string, OwnedBlock>,
  flowName: string,
): FlowOwnerResolution | null {
  const flowBlocks = blocks as Record<string, { flow?: Record<string, unknown> }>

  // 1. Declaration — full name, then bare name. The declared key is returned
  //    so saves merge into the existing entry instead of duplicating the flow
  //    under a namespaced alias.
  const direct = findFlowOwner(flowBlocks, flowName)
  if (direct) return { blockName: direct.blockName, flowKey: flowName, flow: direct.flow }

  const bare = bareFlowName(flowName)
  if (bare) {
    const bareOwner = findFlowOwner(flowBlocks, bare)
    if (bareOwner) return { blockName: bareOwner.blockName, flowKey: bare, flow: bareOwner.flow }
  }

  // 2. Event binding — a flow created in the Behavior surface is bound to an
  //    element event before its first step is saved; that block owns it.
  //    flowKey must be the SAME name the binding references: reload matching
  //    tolerates a flow being more namespaced than its binding, not less, so
  //    saving a full-name-bound flow under the bare key would orphan the event.
  const eventBlocks = blocks as Record<string, { events?: Record<string, Record<string, EventBinding>> }>
  const fullTrigger = findFlowTriggers(eventBlocks, flowName)[0]
  if (fullTrigger) return { blockName: fullTrigger.blockName, flowKey: flowName }
  if (bare) {
    const bareTrigger = findFlowTriggers(eventBlocks, bare)[0]
    if (bareTrigger) return { blockName: bareTrigger.blockName, flowKey: bare }
  }

  return null
}
